'use client'
import Button from "@/components/inputs/button";
import { useDispatch, useSelector } from "react-redux";
import { SET_ACTIVE_SIGNATURE, SET_PAYMENT_DATA, SET_PAYMENT_ID_PEDIDO } from "@/store/slices/paymentSlice";
import InputWithValidation from "@/components/inputs/inputWithValidation";
import { PaymentTransform } from "@/utils/transform/payment";

import {
    GET_ALL_STATE,
    GET_USER_STEP,
    SET_LOADING,
    SET_MODAL,
    SET_RECAPTCHA,
    SET_USER_STEP
} from "@/store/slices/appSlice";
import { useEffect, useRef, useState } from "react";
import ContratoAssinatura from "@/components/texts/contratoAssinatura";

import Events from "@/utils/Events";
import { SET_LEAD_CELULAR, SET_LEAD_NOME, SET_LEAD_ORIGEM_VENDA, SET_LEAD_TENTATIVA_TEF, SET_LEAD_PARCELA } from "@/store/slices/leadSlice";
import Lead from "@/utils/Lead";
import { GET_OFFER, GET_OFFER_TYPE } from "@/store/slices/offerSlice";
import Apm from "@/utils/Apm";
import { GET_USER, IUserValue, SET_USER } from "@/store/slices/userSlice";
import { useCookies } from "react-cookie";
import { createPedido } from "@/api/assine";
import { blocked } from "@/utils/EmailBlockedList";
import Nullable from "@/types/utils/Nullable";
import { getCardFlag, isCreditCardCVV, isCreditCardExpirationDate, isCreditCardNumber, isDocument, isName, type CardFlag } from "@/utils/validation";
import { SendDataSubscribe } from "@/utils/SendDataSubscribe";
import Select from "@/components/Select";
import { isProduction } from "@/utils/environment";
import { Col, Row } from "@/features/Endereco";
import useRecaptcha from "@/hooks/useRecaptcha";
import CardFlagIcon from "@/components/CardFlagIcon";

const RECAPTCHA_VALIDATION_TIMEOUT = 30000; // 30 segundos timeout total
const RECAPTCHA_RETRY_DELAY = 3000; // 3 segundos

export default function CartaoCredito() {
    const events = new Events();
    const dispatch = useDispatch();
    const offer_type = useSelector(GET_OFFER_TYPE)
    const oferta = useSelector(GET_OFFER)
    const userStep = useSelector(GET_USER_STEP)
    const isOfertaParcelada = oferta.zterm?.length > 1
    const [numero, setNumero] = useState('')
    const [cardFlag, setCardFlag] = useState<CardFlag  | null>(null)
    const [documento, setDocumento] = useState('')
    const [titular, setTitular] = useState('')
    const [validade, setValidade] = useState('')
    const parcelasTotais = [oferta.zterm]
    const [parcela, setParcela] = useState('CC01')
    const [cvv, setCVV] = useState('')
    const [numeroErrorMessage, setNumeroErrorMessage] = useState<Nullable<string>>(null)
    const [documentoErrorMessage, setDocumentoErrorMessage] = useState<Nullable<string>>(null)
    const [titularErrorMessage, setTitularErrorMessage] = useState<Nullable<string>>(null)
    const [validadeErrorMessage, setValidadeErrorMessage] = useState<Nullable<string>>(null)
    const [cvvErrorMessage, setCVVErrorMessage] = useState<Nullable<string>>(null)
    const states = useSelector(GET_ALL_STATE)
    const [btnDisabled, setBtn] = useState(false);
    const [all_states, setAllStates] = useState(states)
    const USER = useSelector(GET_USER)
    const [cookies, setCookie] = useCookies(['OESP_INFO'])
    const recaptcha = useRecaptcha()

    // Refs para controle de reCAPTCHA
    const recaptchaTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const isRecaptchaValidatingRef = useRef<boolean>(false);
    const recaptchaRetryCountRef = useRef<number>(0);
    const MAX_RECAPTCHA_RETRIES = 2;

    const MIN_DIGITS_TO_IDENTIFY_FLAG = 6;
    const bandeiraErroDisparadoRef = useRef<boolean>(false);

    useEffect(() => {
        const digits = numero.replace(/\D/g, '');
        const flag = getCardFlag(numero);
        setCardFlag(flag);

        if (!flag && digits.length >= MIN_DIGITS_TO_IDENTIFY_FLAG) {
            setNumeroErrorMessage('Bandeira do cartão inválida/não aceita');
            if (!bandeiraErroDisparadoRef.current) {
                bandeiraErroDisparadoRef.current = true;
                events.trigger('pagamento_cartao_erro_bandeira_do_cartao', { offer_type })
            }
        } else if (flag && numeroErrorMessage === 'Bandeira do cartão inválida/não aceita') {
            setNumeroErrorMessage(null);
        }

        if (flag || digits.length < MIN_DIGITS_TO_IDENTIFY_FLAG) {
            bandeiraErroDisparadoRef.current = false;
        }
    }, [numero])

    const bandeiraNaoReconhecida = numero.replace(/\D/g, '').length >= MIN_DIGITS_TO_IDENTIFY_FLAG && !cardFlag

    let value_offer = oferta.has_partner == true ? 0 : oferta.valor?.valor || oferta.valor?.original;
    const ecommerce = {
        item_id: oferta.cd_oferta,
        item_name: oferta.name,
        price: oferta.valor?.original || oferta.valor?.valor
    }

    const handleSelectChange = (e: any) => {
        dispatch(SET_LEAD_PARCELA(e.target.value))
        setParcela(e.target.value);
    };
    useEffect(() => {
        setAllStates(states)
    }, [states]);

    // Função para limpar timeouts e refs de reCAPTCHA
    // Função para limpar timeouts e refs de reCAPTCHA
    const cleanupRecaptchaRefs = () => {
        if (recaptchaTimeoutRef.current) {
            clearTimeout(recaptchaTimeoutRef.current);
            recaptchaTimeoutRef.current = null;
        }
        isRecaptchaValidatingRef.current = false;
        recaptchaRetryCountRef.current = 0;
        // PREVENTIVA: Garantir reset após cleanup
        try {
            recaptcha.reset();
        } catch (error) {
            console.warn('Erro ao resetar reCAPTCHA durante cleanup:', error);
        }
    };


    // Função para validar reCAPTCHA com retry logic
    const validateRecaptchaWithRetry = async (): Promise<{ token: string, action: string } | undefined> => {
        try {
            // Preventiva 1: Evitar múltiplas validações simultâneas
            if (isRecaptchaValidatingRef.current) {
                Apm.captureError('reCAPTCHA: Validação já em progresso - operação bloqueada');
                throw new Error('Validação de reCAPTCHA já em progresso');
            }

            isRecaptchaValidatingRef.current = true;
            recaptchaRetryCountRef.current = 0;

            // PREVENTIVA: Reset antes de iniciar nova validação
            try {
                recaptcha.reset();
            } catch (error) {
                console.warn('Aviso: Erro ao resetar reCAPTCHA antes da validação:', error);
            }

            // Preventiva 2: Timeout geral de validação
            const validationPromise = new Promise<{ token: string, action: string }>((resolve, reject) => {
                let timeoutId: NodeJS.Timeout | null = null;

                const attemptValidation = async (retryCount: number = 0) => {
                    try {
                        // Aguardar delay entre tentativas
                        if (retryCount > 0) {
                            // IMPORTANTE: Resetar ANTES de aguardar
                            try {
                                recaptcha.reset();
                            } catch (resetError) {
                                console.warn(`Erro ao resetar reCAPTCHA no retry ${retryCount}:`, resetError);
                            }

                            // Aguardar o delay com buffer para garantir reset
                            await new Promise(r => setTimeout(r, RECAPTCHA_RETRY_DELAY + 500));
                        }

                        recaptchaRetryCountRef.current = retryCount;
                        const token = await recaptcha.validate('CHECKOUT');

                        if (!token) {
                            throw new Error('Token do reCAPTCHA não foi gerado');
                        }

                        if (timeoutId) {
                            clearTimeout(timeoutId);
                            recaptchaTimeoutRef.current = null;
                        }
                        resolve({ token, action: 'CHECKOUT' });
                    } catch (error: any) {
                        const errorMsg = error?.message || 'Erro desconhecido';

                        // Preventiva 3: Detectar erro DUPE e tentar novamente
                        if (errorMsg.includes('DUPE') && retryCount < MAX_RECAPTCHA_RETRIES) {
                            Apm.captureError(`reCAPTCHA DUPE detectado. Tentativa ${retryCount + 1}/${MAX_RECAPTCHA_RETRIES}`);
                            // Chamada recursiva com incremento
                            attemptValidation(retryCount + 1);
                        } else if (errorMsg.includes('DUPE')) {
                            if (timeoutId) {
                                clearTimeout(timeoutId);
                                recaptchaTimeoutRef.current = null;
                            }
                            reject(new Error(`reCAPTCHA DUPE após ${MAX_RECAPTCHA_RETRIES} tentativas`));
                        } else {
                            if (timeoutId) {
                                clearTimeout(timeoutId);
                                recaptchaTimeoutRef.current = null;
                            }
                            reject(error);
                        }
                    }
                };

                // Inicializar timeout
                timeoutId = setTimeout(() => {
                    recaptchaTimeoutRef.current = null;
                    reject(new Error('Timeout na validação de reCAPTCHA'));
                }, RECAPTCHA_VALIDATION_TIMEOUT);
                recaptchaTimeoutRef.current = timeoutId;

                // Iniciar primeira tentativa
                attemptValidation(0);
            });

            return await validationPromise;
        } catch (error: any) {
            const errorMsg = error?.message || 'Erro desconhecido';

            // PREVENTIVA: Sempre resetar após erro
            try {
                recaptcha.reset();
            } catch (resetError) {
                console.warn('Erro ao resetar reCAPTCHA após falha:', resetError);
            }

            if (errorMsg.includes('DUPE')) {
                Apm.captureError(`reCAPTCHA DUPE: ${errorMsg}`);
                throw new Error('reCAPTCHA inválido: DUPE');
            } else if (errorMsg.includes('Timeout')) {
                Apm.captureError(`reCAPTCHA Timeout: ${errorMsg}`);
                throw new Error('Validação de reCAPTCHA expirou. Tente novamente.');
            } else {
                Apm.captureError(`reCAPTCHA Error: ${errorMsg}`);
                throw error;
            }
        } finally {
            isRecaptchaValidatingRef.current = false;
        }
    };

    async function saveToState(event: any) {
        event.preventDefault()
        let recaptchaObj: { token: string, action: string } | undefined = undefined;

        try {
            if (btnDisabled) {
                return
            }
            setBtn(true)

            // Validar reCAPTCHA com retry
            try {
                recaptchaObj = await validateRecaptchaWithRetry();

                if (recaptchaObj?.token) {
                    dispatch(SET_RECAPTCHA(recaptchaObj.token))
                }
            } catch (error) {
                // Tratar erro específico de DUPE
                if (error instanceof Error && error.message.includes('DUPE')) {
                    dispatch(SET_MODAL({
                        show: true,
                        type: 'generic-error',
                        message: 'Sua tentativa expirou. Por favor, recarregue a página e tente novamente.'
                    }));
                    Apm.captureError('reCAPTCHA DUPE: Token reutilizado ou expirado')
                } else if (error instanceof Error && error.message.includes('Timeout')) {
                    dispatch(SET_MODAL({
                        show: true,
                        type: 'generic-error',
                        message: 'Validação de segurança expirou. Por favor, tente novamente.'
                    }));
                } else {
                    Apm.captureError(`reCAPTCHA Error: ${error}`);
                }
                dispatch(SET_LOADING(false))
                setBtn(false)
                recaptchaObj = undefined;
                cleanupRecaptchaRefs();
                return
            }

            let erroNoFormulario: boolean = false
            if (!isCreditCardNumber(numero)) {
                erroNoFormulario = true
                events.trigger('pagamento_cartao_erro_numero_do_cartao', { offer_type })
                setNumeroErrorMessage('Cartão de Crédito Inválido')
            }
            if (!isCreditCardCVV(cvv)) {
                erroNoFormulario = true
                events.trigger('pagamento_cartao_erro_codigo_de_seguranca', { offer_type })
                setCVVErrorMessage('CVV Inválido')
            }
            if (!isCreditCardExpirationDate(validade)) {
                erroNoFormulario = true
                events.trigger('pagamento_cartao_erro_validade_do_cartao', { offer_type })
                setValidadeErrorMessage('Validade Inválida')
            }
            if (!isName(titular)) {
                erroNoFormulario = true
                events.trigger('pagamento_cartao_erro_nome', { offer_type })
                setTitularErrorMessage('Nome Inválido')
            }
            if (!isDocument(documento)) {
                erroNoFormulario = true
                events.trigger('pagamento_cartao_erro_documento', { offer_type })
                setDocumentoErrorMessage('Documento Inválido')
            }
            if (erroNoFormulario) {
                dispatch(SET_LOADING(false))
                setBtn(false)
                cleanupRecaptchaRefs();
                return
            }

            if (!USER.nome) {
                try {
                    USER.nome = titular;
                    dispatch(SET_USER(USER))
                    dispatch(SET_LEAD_NOME(titular))
                } catch (error) {
                    Apm.captureError(`CENÁRIO: USUÁRIO SEM NOME CADASTRADO, ERRO: FALHA NO CADASTRO DO NOME COM BASE NO NOME DO CARTAO, erro catch: ${error}`)
                }
            }
            setCVVErrorMessage('')
            setDocumentoErrorMessage('')
            setNumeroErrorMessage('')
            setTitularErrorMessage('')
            setValidadeErrorMessage('')

            let telefone = null;
            try {
                let dispatched = false;
                if (typeof USER !== 'undefined' && typeof USER.telefone !== 'undefined') {
                    dispatched = true;
                    telefone = USER.telefone
                    dispatch(SET_LEAD_CELULAR(USER.telefone))
                }
                if (!dispatched) {
                    const oesp_info = JSON.parse(cookies?.OESP_INFO);
                    if (oesp_info && oesp_info.telefone) {
                        telefone = oesp_info.telefone
                        dispatch(SET_LEAD_CELULAR(oesp_info.telefone))
                    }
                }

            } catch (error) {
                Apm.captureError(`CartaoCredito() -> saveToState() FALHA AO CAPTURAR O TELEFONE DO USUÁRIO: ${error}`)
            }

            let data: any = {
                numero,
                documento,
                titular,
                validade,
                cvv
            }
            if (isOfertaParcelada) {
                data.parcela = parcela
            }
            const objectToSend = new PaymentTransform(all_states, 'cc', new URL(window.location.toString()), data)
            dispatch(SET_LOADING(true))
            const objSend: any = objectToSend.process();
            if (objSend && typeof objSend.responsavel === 'object') {
                objSend.responsavel.celular = telefone
                objSend.responsavel.nome = USER.nome || titular?.split(' ')[0] || ''
                objSend.responsavel.sobrenome = USER.sobre_nome
            }

            events.trigger('add_payment_info', {
                event: 'add_payment_info',
                value: value_offer,
                ecommerce: ecommerce
            });

            objSend.recaptcha = recaptchaObj

            if (await blocked(objSend.responsavel.email)) {
                dispatch(SET_MODAL({ show: true, type: 'pagamento-duplicado' }))
                dispatch(SET_LOADING(false))
                setBtn(false)
                cleanupRecaptchaRefs();
                return
            }

            createPedido(objSend, true).then(async (res) => {
                if (
                    res &&
                    typeof res === 'object' &&
                    'errors' in res &&
                    Array.isArray((res as any).errors) &&
                    (res as any).errors.length > 0
                ) {
                    const validationMsg = (res as any).errors[0]?.message || 'Erro de validação no pagamento';
                    setNumeroErrorMessage(validationMsg);
                    dispatch(SET_MODAL({ type: "falha-pagamento", show: true }))
                    events.trigger("pagamento_cartao_negado", { offer_type })
                    setBtn(false);
                    // PREVENTIVA: Reset após uso do token (mesmo com erro)
                    cleanupRecaptchaRefs();
                    return;
                }
                if (typeof res === 'number') {
                    Apm.captureError('ERRO NO CARTAOCREDITO A RESPOSTA ERA DO TIPO NUMBER')
                    cleanupRecaptchaRefs();
                    return
                }

                if (typeof res === 'string') {
                    Apm.captureError('ERRO NO CARTAOCREDITO A RESPOSTA ERA DO TIPO STRING')
                    cleanupRecaptchaRefs();
                    return
                }

                if ((res as any)?.activate_signature === true && res?.id_pedido) {
                    document.cookie = `venda_suspeita=true;path=/;domain=.estadao.com.br;max-age=90000`;
                    document.cookie = `id_pedido=${res.id_pedido};path=/;domain=.estadao.com.br;max-age=90000`;
                }

                Apm.addLabels({
                    idPedido: res.id_pedido,
                    id_transacao: res.id_transacao
                });

                Events.sendHotjarEvent("id_pedido_" + res.id_pedido);

                events.trigger('add_payment_info', {
                    event: 'add_payment_info',
                    value: value_offer,
                    ecommerce: ecommerce
                });

                if (res.id_pedido) {
                    if (isOfertaParcelada) {
                        dispatch(SET_LEAD_PARCELA(parcela))
                    }

                    dispatch(SET_PAYMENT_DATA(data))
                    dispatch(SET_PAYMENT_ID_PEDIDO(res.id_pedido))
                    dispatch(SET_LEAD_ORIGEM_VENDA(res.id_pedido))
                    dispatch(SET_LEAD_TENTATIVA_TEF('S'))
                    dispatch(SET_ACTIVE_SIGNATURE(res.activate_signature))
                    Lead.save(states?.lead?.value, dispatch);
                    dispatch(SET_USER_STEP(userStep + 1))
                    await SendDataSubscribe({
                        orderid: res.id_pedido,
                        pageview: window.location.href,
                        timestamp: new Date().getTime().toString(),
                        useragent: navigator.userAgent,
                        userid: cookies.OESP_INFO.id,
                        gtm: typeof (window as any).google_tag_manager == 'undefined' ? "não" : "sim"
                    })
                }
                // PREVENTIVA: Reset após sucesso
                cleanupRecaptchaRefs();
            }).catch((error) => {
                dispatch(SET_LOADING(false))
                setBtn(false)
                try {
                    if (
                        error &&
                        typeof error === 'object' &&
                        'errors' in error &&
                        Array.isArray((error as any).errors) &&
                        (error as any).errors.length > 0
                    ) {
                        const validationMsg = (error as any).errors[0]?.message || 'Erro de validação no pagamento';
                        setNumeroErrorMessage(validationMsg);
                        dispatch(SET_MODAL({ type: "falha-pagamento", show: true }))
                        events.trigger("pagamento_cartao_negado", { offer_type })
                        cleanupRecaptchaRefs();
                        return;
                    }
                    Apm.captureError(error);
                    dispatch(SET_LEAD_TENTATIVA_TEF('S'))
                    Lead.save(states.lead.value, dispatch);

                    if (error && error.hasOwnProperty('response') && error.response.hasOwnProperty('status') && error.response.status === 422) {
                        // Tratar erro específico de reCAPTCHA inválido (DUPE)
                        const responseMsg = error.response.data?.message || '';
                        if (responseMsg.includes('DUPE') || responseMsg.includes('recaptcha') || responseMsg.includes('reCAPTCHA')) {
                            dispatch(SET_MODAL({
                                type: "generic-error",
                                show: true,
                                message: 'Sua tentativa de pagamento expirou. Por favor, recarregue a página e tente novamente.'
                            }))
                            Apm.captureError(`API retornou 422 - possível DUPE: ${responseMsg}`)
                        }
                    } else if (error && error.hasOwnProperty('response') && error.response.hasOwnProperty('status') && error.response.status === 409) {
                        dispatch(SET_MODAL({ type: "pagamento-duplicado", show: true }))
                    } else if (error && error.hasOwnProperty('response') && error.response.hasOwnProperty('status') && (error.response.status === 402 || error.response.status === 401)) {
                        dispatch(SET_MODAL({ type: "falha-pagamento", show: true }))
                    } else if (error && error.response && error.response.status === 201) {
                        dispatch(SET_USER_STEP(userStep + 1))
                    }
                } catch (e) {
                    dispatch(SET_LOADING(false));
                    setBtn(false)
                }
                // PREVENTIVA: Reset após erro
                cleanupRecaptchaRefs();
            }).finally(() => {
                dispatch(SET_LOADING(false))
                setBtn(false)
                // PREVENTIVA: Garantir reset no finally
                cleanupRecaptchaRefs();
            })
        } catch (err) {
            dispatch(SET_LOADING(false));
            setBtn(false);
            // PREVENTIVA: Reset após exceção geral
            cleanupRecaptchaRefs();
        }
    }

    // Limpar refs ao desmontar o componente
    useEffect(() => {
        return () => {
            cleanupRecaptchaRefs();
        };
    }, []);

    // @ts-ignore
    return <form onSubmit={saveToState}>
        <InputWithValidation
            name="titular"
            id="titular"
            type="text"
            placeholder="Nome do titular"
            OnClick={() => {
                events.trigger("pagamento_cartao_nome", { offer_type }),
                    setTitularErrorMessage(null)
            }}
            onChange={setTitular}
            errorMessage={titularErrorMessage}
            value={titular}
        />
        <InputWithValidation
            name="documento"
            id="documento"
            type="text"
            placeholder="CPF ou CNPJ"
            OnClick={() => {
                events.trigger("pagamento_cartao_documento", { offer_type }),
                    setDocumentoErrorMessage(null)
            }}
            onChange={setDocumento}
            errorMessage={documentoErrorMessage}
            value={documento}
        />
        <div className="input-flag-wrapper">
            <InputWithValidation
                name="numero"
                id="numero"
                type="text"
                placeholder="Número do Cartão"
                OnClick={() => {
                    events.trigger("pagamento_cartao_numero_do_cartao", { offer_type }),
                        setNumeroErrorMessage(null)
                }}
                onChange={setNumero}
                errorMessage={numeroErrorMessage}
                value={numero}
            />
            <div className="card-flag">
                {/*
                    Enquanto o input estiver invalido quem aparece e o icone de
                    alerta. Ao clicar no input o erro e limpado e a bandeira volta.
                */}
                <CardFlagIcon flag={numeroErrorMessage ? null : cardFlag} />
            </div>
        </div>
        <Row>
            <Col width="40%">
                <InputWithValidation
                    name="validade"
                    id="validade"
                    type="text"
                    placeholder="Validade"
                    OnClick={() => {
                        events.trigger("pagamento_cartao_validade_do_cartao", { offer_type }),
                            setValidadeErrorMessage(null)
                    }}
                    onChange={setValidade}
                    errorMessage={validadeErrorMessage}
                    value={validade}
                />
            </Col>
            <Col width="60%">
                <InputWithValidation
                    name="cvv"
                    id="cvv"
                    type="text"
                    placeholder="Cód. de Segurança"
                    OnClick={() => {
                        events.trigger("pagamento_cartao_codigo_de_seguranca", { offer_type }),
                            setCVVErrorMessage(null)
                    }}
                    onChange={setCVV}
                    errorMessage={cvvErrorMessage}
                    value={cvv}
                />
            </Col>
        </Row>
        {isOfertaParcelada &&
            <Select
                name="parcelas"
                type="text"
                id='parcelas'
                options={parcelasTotais}
                OnClick={() => {
                    events.trigger("pagamento_cartao_parcelas", { offer_type })
                }}
                onChange={handleSelectChange}
            />
        }
        <Button id="continuar_step_2" data-transaction-name="pagamento_cartao_concluir" type={'submit'} disabled={bandeiraNaoReconhecida || (isProduction() && btnDisabled)} onClick={() => { events.trigger("pagamento_cartao_concluir", { offer_type }) }}>Concluir Pagamento</Button>
        <div className={'text--center'}>
            <ContratoAssinatura paymentMethod="cartao"></ContratoAssinatura>
        </div>
    </form>
}
